import React, { useEffect, useRef } from 'react';

interface StarfieldProps {
  onActivity: () => void;
}

interface Star {
  x: number;
  y: number;
  z: number;
  prevX: number;
  prevY: number;
}

export const Starfield: React.FC<StarfieldProps> = ({ onActivity }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const starsRef = useRef<Star[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const initStars = () => {
      starsRef.current = [];
      for (let i = 0; i < 800; i++) {
        starsRef.current.push({
          x: Math.random() * 2000 - 1000,
          y: Math.random() * 2000 - 1000,
          z: Math.random() * 1000,
          prevX: 0,
          prevY: 0
        });
      }
    };

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const speed = 5; // Reduced from 15 to make stars more visible
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    const animate = () => {
      // Create trail effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      starsRef.current.forEach(star => {
        star.prevX = star.x / star.z * 100 + centerX;
        star.prevY = star.y / star.z * 100 + centerY;

        star.z -= speed;

        if (star.z <= 0) {
          star.x = Math.random() * 2000 - 1000;
          star.y = Math.random() * 2000 - 1000;
          star.z = 1000;
        }

        const x = star.x / star.z * 100 + centerX;
        const y = star.y / star.z * 100 + centerY;

        const opacity = (1000 - star.z) / 1000;
        const size = (1000 - star.z) / 1000 * 3;

        ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.lineWidth = size;
        ctx.beginPath();
        ctx.moveTo(star.prevX, star.prevY);
        ctx.lineTo(x, y);
        ctx.stroke();

        // Draw star point
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.beginPath();
        ctx.arc(x, y, size / 2, 0, Math.PI * 2);
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black cursor-none z-[9999]"
      onClick={onActivity}
      onMouseMove={onActivity}
      onKeyDown={onActivity}
    >
      <canvas 
        ref={canvasRef}
        className="w-full h-full"
      />
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white text-lg opacity-60">
        Traveling through hyperspace... Move mouse or press any key to exit
      </div>
    </div>
  );
};